import type { FollowUpPriority, Visit } from './visit.js'

/**
 * Which way an appointment's status may move from the console, and the order
 * follow-up priorities rank in — with NO runtime dependencies.
 *
 * Its own module, reachable as `@fitting-room/contracts/appointment-status`,
 * for the reason `./calendar-marks.ts` is: the console draws its status
 * buttons and its right-click menu from this table, and the API refuses
 * anything off it, so both have to read the SAME table. tperent kept two
 * copies and a test that parsed one out of the other's source; one module both
 * sides import is the same guarantee without the parser.
 */

type VisitStatus = Visit['status']

/**
 * From → where the console may take it.
 *
 * Everything leaves `booked`, and everything can be undone back to it. Ported
 * from tperent's table (`ALLOWED_STATUS_TRANSITIONS`), which learned the
 * second half the hard way: "Mark completed" sits next to "Cancel", and a
 * status with no way back is a mis-click that stays on the calendar forever.
 *
 * Unlike tperent, canceled and no-show come back too. There it was refused
 * because canceling had already emailed the customer; here it sends nothing —
 * Koalendar owns the booking and its emails, and a reminder still pending is
 * skipped at send time by reading the status (`notifications/runner.ts`), so
 * putting an appointment back to booked puts its reminders back with it.
 *
 * `completed → no_show` and the like are absent on purpose: correcting one
 * outcome into another goes through `booked`, which is two clicks a stylist
 * makes knowingly rather than one she makes by accident.
 */
export const VISIT_STATUS_TRANSITIONS: Readonly<Record<VisitStatus, readonly VisitStatus[]>> = {
  booked: ['completed', 'no_show', 'cancelled'],
  completed: ['booked'],
  no_show: ['booked'],
  cancelled: ['booked'],
}

export function canMoveVisitStatus(from: VisitStatus, to: VisitStatus): boolean {
  return VISIT_STATUS_TRANSITIONS[from].includes(to)
}

/** Most pressing first: the order the follow-up queue breaks a tie on one day in. */
export const FOLLOW_UP_PRIORITY_ORDER: readonly FollowUpPriority[] = ['urgent', 'high', 'normal', 'low']

/** 0 for urgent, 3 for low. A visit with no priority ranks as `normal`. */
export function followUpPriorityRank(priority: FollowUpPriority | undefined): number {
  return FOLLOW_UP_PRIORITY_ORDER.indexOf(priority ?? 'normal')
}

/**
 * Whether the customer bought: an order on the appointment that is not
 * canceled. The order is the answer — the owner's word, 2026-10-15 — so the
 * console no longer asks a stylist to say "Sold" beside it, and the stored
 * `VisitOutcome` is no longer what the back office reads.
 */
export function hasLiveOrder(orders: ReadonlyArray<{ readonly status: string }>): boolean {
  return orders.some((order) => order.status !== 'canceled')
}

/**
 * Whether to ask why the customer didn't buy: they came (the appointment is
 * completed) and left with no order. Before the appointment, or after a
 * cancellation or a no-show, there is nothing to explain.
 */
export function asksWhyNoPurchase(
  visit: {
    readonly status: VisitStatus
    readonly orders: ReadonlyArray<{ readonly status: string }>
  },
  /** A pickup is the customer collecting what they bought: there is no sale to explain the absence of. */
  pickup = false,
): boolean {
  return !pickup && visit.status === 'completed' && !hasLiveOrder(visit.orders)
}
